import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { StorageService } from '../storage/storage.service';
import { StoreConfigurationRepositoryService } from '../store-configuration/store-configuration-repository.service';

@Injectable({
  providedIn: 'root'
})
export class AdsManagementService {
  private route = inject(ActivatedRoute);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);

  serverSideGoogleTrackingEnabled = this.storeConfigurationRepositoryService.storeConfigurations()?.google_ads?.enable_server_side_tracking ?? false;

  manageUtmAndCodeDetailsFromUrl() {
    combineLatest([
      this.route.queryParams,
      this.router.events.pipe(filter((i) => i instanceof NavigationEnd))
    ])
      .subscribe(([_queryParams, route]) => {
        // url params stored on localStorage
        const {
          code = '',
          utm_medium = [],
          utm_source = [],
          utm_campaign = [],
          fbclid = [],
          gclid = [],
          fbp = []
        } = this.storageService.retrieve('urlUtmDetails') || {};

        // mapping url properties
        let urlUtmMediumToUpdate = '';
        let urlUtmSourceToUpdate = '';
        let urlUtmCampaignToUpdate = '';
        let urlCodeToUpdate = '';
        let urlFbclidToUpdate = '';
        let urlGclidToUpdate = '';
        let urlFbpToUpdate = '';

        Object.keys(_queryParams).map((param) => {
          if (param.toLowerCase() === 'utm_medium') {
            urlUtmMediumToUpdate = _queryParams[param];
          } else if (param.toLowerCase() === 'utm_source') {
            urlUtmSourceToUpdate = _queryParams[param];
          } else if (param.toLowerCase() === 'utm_campaign') {
            urlUtmCampaignToUpdate = _queryParams[param];
          } else if (param.toLowerCase() === 'code') {
            urlCodeToUpdate = _queryParams[param];
          } else if (param.toLowerCase() === 'fbclid') {
            urlFbclidToUpdate = _queryParams[param];
          } else if (param.toLowerCase() === 'gclid') {
            urlGclidToUpdate = _queryParams[param];
          } else if (param.toLowerCase() === 'fbp') {
            urlFbpToUpdate = _queryParams[param];
          }
        });

        let updatedUtmSrc = [];
        let updatedUtmMedium = [];
        let updatedUtmCampaign = [];
        let updatedFbclid = [];
        let updatedGclid = [];
        let updatedFbp = [];

        // store utm_source / manage append on different utm_source
        if (
          !utm_source.includes(urlUtmSourceToUpdate) &&
          urlUtmSourceToUpdate?.length
        ) {
          updatedUtmSrc = [...utm_source, urlUtmSourceToUpdate];
        } else {
          updatedUtmSrc = [...utm_source];
        }

        // store utm_medium / manage append on different utm_medium
        if (
          !utm_medium.includes(urlUtmMediumToUpdate) &&
          urlUtmMediumToUpdate
        ) {
          updatedUtmMedium = [...utm_medium, urlUtmMediumToUpdate];
        } else {
          updatedUtmMedium = [...utm_medium];
        }

        // store utm_campaign / manage append on different utm_campaign
        if (
          !utm_campaign.includes(urlUtmCampaignToUpdate) &&
          urlUtmCampaignToUpdate
        ) {
          updatedUtmCampaign = [...utm_campaign, urlUtmCampaignToUpdate];
        } else {
          updatedUtmCampaign = [...utm_campaign];
        }

        // store fbclid / manage append on different fbclid
        if (
          !fbclid.includes(urlFbclidToUpdate) &&
          urlFbclidToUpdate
        ) {
          updatedFbclid = [...fbclid, urlFbclidToUpdate];
        } else {
          updatedFbclid = [...fbclid];
        }

        // store gclid / manage append on different gclid
        if (
          !gclid.includes(urlGclidToUpdate) &&
          urlGclidToUpdate
        ) {
          updatedGclid = [...gclid, urlGclidToUpdate];
        } else {
          updatedGclid = [...gclid];
        }

        // store fbp / manage append on different fbp
        if (
          !fbp.includes(urlFbpToUpdate) &&
          urlFbpToUpdate
        ) {
          updatedFbp = [...fbp, urlFbpToUpdate];
        } else {
          updatedFbp = [...fbp];
        }

        // set url details on localStorage
        this.storageService.save('urlUtmDetails', {
          code: urlCodeToUpdate || code,
          utm_source: updatedUtmSrc,
          utm_medium: updatedUtmMedium,
          utm_campaign: updatedUtmCampaign,
          fbclid: updatedFbclid,
          gclid: updatedGclid,
          fbp: updatedFbp
        });
      });
  }

  getCollectedUtmAndCodeDetails() {
    const { code = '', utm_medium = [], utm_source = [], utm_campaign = [], fbclid = [], gclid = [], fbp = [] } = this.storageService.retrieve('urlUtmDetails') || {};

    return {
      ...(code && { code }),
      ...(utm_medium?.length && { utm_medium: utm_medium.join(',') }),
      ...(utm_source?.length && { utm_source: utm_source.join(',') }),
      ...(utm_campaign?.length && { utm_campaign: utm_campaign.join(',') }),
      ...(fbclid?.length && { fbclid: fbclid.join(',') }),
      ...(gclid?.length && this.serverSideGoogleTrackingEnabled && { gclid: gclid.join(',') }),
      ...(fbp?.length && { fbp: fbp.join(',') })
    };
  }

  linkUtmAndCodeDetailsWithOrder(orderId: string) {
    const collectedUtmAndCodeDetails = this.getCollectedUtmAndCodeDetails();
    if (collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length) {
      this.storageService.save(orderId, collectedUtmAndCodeDetails);
      this.clearUtmAndCodeDetails();
    }
  }

  getOrderUtmAndCodeDetails(orderId: string) {
    return this.storageService.retrieve(orderId);
  }

  clearOrderUtmAndCodeDetails(orderId: string) {
    this.storageService.remove(orderId);
  }

  clearUtmAndCodeDetails() {
    this.storageService.remove('urlUtmDetails');
  }
}
